import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Context Data Display Component
 *
 * Displays contextual data for form compilations, such as patient information,
 * test results, or other source-specific data.
 *
 * @param {Object} props - Component props
 * @param {Object} props.contextData - Context data object from formCompletion
 * @param {Object} props.colors - Color scheme for styling
 * @param {boolean} props.isCollapsed - Whether the panel is collapsed
 * @param {Function} props.onToggleCollapse - Function to toggle collapse state
 * @param {number} props.toolbarHeight - Height of toolbar for sticky positioning
 */
const ContextDataDisplay = ({ contextData, colors, isCollapsed, onToggleCollapse, toolbarHeight }) => {
  const { t } = useTranslation();

  // Don't render if no context data
  if (!contextData || !contextData.source) {
    return null;
  }

  /**
   * Format date for display
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  /**
   * Render an antibiotic table for a specific set of antibiotics
   * @param {Array} antibiotics - Array of antibiotic data
   * @param {boolean} showMicroorganismName - Whether to show the microorganism name in the table
   * @returns {JSX.Element} Table component
   */
  const renderAntibioticTable = (antibiotics, showMicroorganismName = true) => {
    return (
      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.75rem' }}>
          <thead className="table-light">
            <tr>
              <th style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                {t('atcCode', { ns: 'labels', defaultValue: 'Codice ATC' })}
              </th>
              <th style={{ padding: '4px 8px' }}>
                {t('antibiotic', { ns: 'labels', defaultValue: 'Antibiotico' })}
              </th>
              <th style={{ padding: '4px 8px', textAlign: 'center', width: '80px' }}>
                {t('mic', { ns: 'labels', defaultValue: 'MIC' })}
              </th>
              <th style={{ padding: '4px 8px', textAlign: 'center', width: '60px' }}>
                {t('sensitivity', { ns: 'labels', defaultValue: 'S/R/I' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {antibiotics.map((ab, index) => (
              <tr key={index}>
                <td style={{ padding: '4px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {ab.code || '-'}
                </td>
                <td style={{ padding: '4px 8px' }}>
                  {ab.name}
                  {showMicroorganismName && ab.microorganismName && (
                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                      vs {ab.microorganismName}
                    </small>
                  )}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'center', fontFamily: 'monospace' }}>
                  {ab.mic || '-'}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                  <span className={`badge ${
                    ab.sensitivity === 'S' ? 'bg-success' : 
                    ab.sensitivity === 'R' ? 'bg-danger' : 
                    'bg-warning text-dark'
                  }`} style={{ fontSize: '0.7rem' }}>
                    {ab.sensitivity || '-'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  /**
   * Render content based on source type
   */
  const renderContextContent = () => {
    switch (contextData.source) {
      case 'lis_infection_alert':
        return (
          <div className="context-data-content">
            {/* Header with alert icon */}
            <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
              <div
                className="d-flex align-items-center justify-content-center rounded bg-light me-3"
                style={{ width: '40px', height: '40px' }}
              >
                <i className="fa-solid fa-virus text-danger fa-lg"></i>
              </div>
              <div>
                <h6 className="mb-0 text-dark">
                  {t('infectionAlert', { ns: 'labels', defaultValue: 'Allerta Infezione' })}
                </h6>
                <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {t('microbiologicalTestDetection', { ns: 'labels', defaultValue: 'Rilevamento da test microbiologico' })}
                </small>
              </div>
            </div>

            {/* Business Unit */}
            {(contextData.businessUnit && contextData.businessUnit.name) && (
              <div className="mb-2">
                <small className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>
                  {t('businessUnit', { ns: 'labels', defaultValue: 'Unità Operativa' })}
                </small>
                <div className="mt-1 text-dark">
                  {contextData.businessUnit.name}
                </div>
              </div>
            )}

            {/* Patient Information with 3-letter code */}
            {(contextData.patient && (contextData.patient.lastName || contextData.patient.name)) && (
              <div className="mb-2">
                <small className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>
                  {t('patient', { ns: 'labels', defaultValue: 'Paziente' })}
                </small>
                <div className="d-flex align-items-center mt-1">
                  <span className="fw-medium text-dark">
                    {(contextData.patient.lastName || '').substring(0, 3)} {(contextData.patient.name || '').substring(0, 3)}
                  </span>
                  {contextData.patient.sex && (
                    <span className="badge bg-light text-dark ms-2 border">
                      {contextData.patient.sex}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* LIS Report ID (ID Referto dell'Ente) */}
            {contextData.lisId && (
              <div className="mb-2">
                <small className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>
                  {t('reportId', { ns: 'labels', defaultValue: 'ID Referto' })}
                </small>
                <div className="mt-1 text-dark">
                  {contextData.lisId}
                </div>
              </div>
            )}

            {/* Test Acceptance Date (Data Accettazione) */}
            {(contextData.testDate || (contextData.test && contextData.test.date)) && (
              <div className="mb-2">
                <small className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>
                  {t('acceptanceDate', { ns: 'labels', defaultValue: 'Data Accettazione' })}
                </small>
                <div className="mt-1 text-dark">
                  {formatDate(contextData.testDate || contextData.test?.date)}
                </div>
              </div>
            )}

            {/* All Microorganisms Isolated */}
            {((contextData.microorganisms && contextData.microorganisms.length > 0) || contextData.microorganism) && (
              <div className="mb-2">
                <small className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>
                  {t('microorganismsIsolated', { ns: 'labels', defaultValue: 'Microrganismi Isolati' })}
                </small>
                {contextData.microorganisms && contextData.microorganisms.length > 0 ? (
                  <div className="mt-1">
                    {contextData.microorganisms.map((mo, index) => (
                      <div key={index} className="p-2 mb-1 bg-light rounded text-dark border-start border-3 border-danger">
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{mo.name}</span>
                          {mo.code && <small className="text-muted fw-normal ms-2">{mo.code}</small>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : contextData.microorganism ? (
                  <div className="mt-1 p-2 bg-light rounded text-dark border-start border-3 border-danger">
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{typeof contextData.microorganism === 'object' ? contextData.microorganism.name : contextData.microorganism}</span>
                      {typeof contextData.microorganism === 'object' && contextData.microorganism.code && (
                        <small className="text-muted fw-normal ms-2">{contextData.microorganism.code}</small>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Biological Material */}
            {(contextData.biologicalMaterial || contextData.material) && (
              <div className="mb-2">
                <small className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>
                  {t('biologicalMaterial', { ns: 'labels', defaultValue: 'Materiale Biologico' })}
                </small>
                <div className="mt-1 text-dark">
                  {(() => {
                    const mat = contextData.material || contextData.biologicalMaterial;
                    return typeof mat === 'object' ? (mat.description || mat.name) : mat;
                  })()}
                </div>
              </div>
            )}

            {/* Antibiotics (Antibiogramma) - Grouped by Microorganism */}
            {contextData.antibiotics && contextData.antibiotics.length > 0 && (
              <div className="mb-2">
                <small className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>
                  {t('antibiotics', { ns: 'labels', defaultValue: 'Antibiogramma' })}
                </small>
                <div className="mt-1">
                  {(() => {
                    // Identify all unique microorganisms mentioned in the antibiotics list
                    // or use the microorganisms array if available
                    const microbesInAntibiotics = Array.from(new Set(
                      contextData.antibiotics
                        .map(ab => ab.microorganismName)
                        .filter(Boolean)
                    ));

                    // If we have explicit microorganisms listed in contextData, use that order
                    let microbeList = [];
                    if (contextData.microorganisms && contextData.microorganisms.length > 0) {
                      microbeList = contextData.microorganisms;
                    } else if (contextData.microorganism) {
                      microbeList = [typeof contextData.microorganism === 'object' ? contextData.microorganism : { name: contextData.microorganism, code: null }];
                    }

                    // Ensure all microbes found in antibiotics are included (if not already there)
                    microbesInAntibiotics.forEach(mName => {
                      if (!microbeList.some(m => m.name === mName)) {
                        microbeList.push({ name: mName, code: null });
                      }
                    });

                    // If still no microbes identified but we have antibiotics, show them all
                    if (microbeList.length === 0) {
                      return renderAntibioticTable(contextData.antibiotics);
                    }

                    // Render a table for each microorganism
                    return microbeList.map((microbe, mIdx) => {
                      const microbeAntibiotics = contextData.antibiotics.filter(
                        ab => ab.microorganismName === microbe.name
                      );

                      if (microbeAntibiotics.length === 0) return null;

                      return (
                        <div key={mIdx} className="mb-3">
                          <div 
                            className="p-2 mb-1 bg-light text-dark border-start border-3 border-primary rounded-end" 
                            style={{ fontSize: '0.75rem' }}
                          >
                            <div className="row g-0">
                              <div className="col-4">
                                <small className="text-muted d-block text-uppercase" style={{ fontSize: '0.6rem' }}>
                                  {t('microorganismCode', { ns: 'labels', defaultValue: 'Codice' })}
                                </small>
                                <span className="fw-bold">{microbe.code || '-'}</span>
                              </div>
                              <div className="col-8">
                                <small className="text-muted d-block text-uppercase" style={{ fontSize: '0.6rem' }}>
                                  {t('microorganismDescription', { ns: 'labels', defaultValue: 'Descrizione Microrganismo' })}
                                </small>
                                <span className="fw-bold text-primary">{microbe.name}</span>
                              </div>
                            </div>
                          </div>
                          {renderAntibioticTable(microbeAntibiotics, false)}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Additional Alert Information */}
            <div className="mt-3 text-muted small fst-italic border-top pt-2">
              <i className="fa-solid fa-info-circle me-1"></i>
              {t('contextDataInfo', {
                ns: 'labels',
                defaultValue: 'Dati acquisiti automaticamente.'
              })}
            </div>
          </div>
        );

      default:
        // Generic context data display for unknown sources
        return (
          <div className="context-data-content">
            <div className="alert alert-light border mb-0">
              <div className="d-flex align-items-center mb-1">
                <i className="fa-solid fa-database text-secondary me-2"></i>
                <strong className="text-dark">{t('contextData', { ns: 'labels', defaultValue: 'Dati di Contesto' })}</strong>
              </div>
              <div>
                <small className="text-muted">
                  {t('source', { ns: 'labels', defaultValue: 'Origine' })}: {contextData.source}
                </small>
              </div>
            </div>
          </div>
        );
    }
  };

  if (isCollapsed) return null;

  return (
    <div
      className="context-data-panel"
      style={{
        position: 'sticky',
        top: `${toolbarHeight}px`,
        height: 'fit-content',
        maxHeight: `calc(100vh - ${toolbarHeight + 20}px)`,
        overflowY: 'auto',
        transition: 'all 0.3s ease',
        zIndex: 100
      }}
    >
      <div className="card shadow-sm border-0">
        {/* Header with collapse toggle */}
        <div
          className="card-header d-flex justify-content-between align-items-center bg-white border-bottom"
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            padding: '1rem'
          }}
          onClick={onToggleCollapse}
        >
          <div className="d-flex align-items-center text-dark">
            <i className="fa-solid fa-circle-info me-2 text-primary"></i>
            <strong style={{ fontSize: '1rem' }}>
              {t('contextInformation', { ns: 'labels', defaultValue: 'Informazioni di Contesto' })}
            </strong>
          </div>
          <button
            className="btn btn-link text-muted p-0"
            style={{
              textDecoration: 'none',
              fontSize: '1rem'
            }}
            aria-label={t('close', { ns: 'labels' })}
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        {/* Body */}
        <div className="card-body p-3">
          {renderContextContent()}
        </div>
      </div>
    </div>
  );
};

export default ContextDataDisplay;
